import type {
  EnrichedSupplierLineItem,
  SupplierInvoiceSummary,
  SupplierInvoiceLineItemsResponse,
  SupplierInvoiceLineItemsFilters,
  SupplierLineItemRuleAssociation,
  ProductGroupTag,
} from '@erp/shared-types';
import useGetApi from '../useGetApi';

// Re-export types for backwards compatibility with existing imports
export type EnrichedLineItem = EnrichedSupplierLineItem;
export type InvoiceSummary = SupplierInvoiceSummary;
export type RuleAssociation = SupplierLineItemRuleAssociation;
export type {
  SupplierInvoiceLineItemsResponse,
  SupplierInvoiceLineItemsFilters,
  ProductGroupTag,
};

/**
 * Hook to fetch enriched invoice line items for a supplier
 * Includes product group tags, rule associations, and invoice numbers
 */
export function useSupplierInvoiceLineItems(
  supplierId: string,
  filters?: SupplierInvoiceLineItemsFilters,
  enabled = true,
) {
  const queryParams: Record<string, string | number | undefined> = {};

  if (filters?.startDate) queryParams.startDate = filters.startDate;
  if (filters?.endDate) queryParams.endDate = filters.endDate;
  if (filters?.divisionIds && filters.divisionIds.length > 0) {
    queryParams.divisionIds = filters.divisionIds.join(',');
  }
  if (filters?.invoiceNumber) {
    queryParams.invoiceNumber = Array.isArray(filters.invoiceNumber)
      ? filters.invoiceNumber.join(',')
      : filters.invoiceNumber;
  }
  if (filters?.rebateableOnly) queryParams.rebateableOnly = 'true';
  if (filters?.limit) queryParams.limit = filters.limit;
  if (filters?.offset) queryParams.offset = filters.offset;
  if (filters?.sortBy) queryParams.sortBy = filters.sortBy;
  if (filters?.sortDirection) queryParams.sortDirection = filters.sortDirection;

  return useGetApi<SupplierInvoiceLineItemsResponse>({
    endpoint: `rebates/supplier-reported-amounts/${supplierId}/line-items`,
    queryParameters: queryParams,
    options: {
      enabled: enabled && !!supplierId,
    },
  });
}
